import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Minus, Plus, Truck, Store, Loader2, MessageSquare } from "lucide-react";

const inputCls = "w-full h-12 rounded-xl border border-brand-green/20 px-4 focus:outline-none focus:ring-2 focus:ring-brand-gold";

const Section = ({ n, title, children }) => (
  <div className="bg-white rounded-2xl p-5 sm:p-6 border border-brand-green/10 shadow-sm">
    <h2 className="font-serif text-xl font-bold text-brand-green mb-4 flex items-center gap-2">
      <span className="w-7 h-7 rounded-full bg-brand-green text-brand-gold text-sm font-sans flex items-center justify-center">{n}</span>
      {title}
    </h2>
    {children}
  </div>
);

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [gamme, setGamme] = useState(params.get("gamme") || "");
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState("livraison");
  const [postal, setPostal] = useState("");
  const [cust, setCust] = useState({ name: "", phone: "", email: "", company: "" });
  const [address, setAddress] = useState("");
  const [floor, setFloor] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data);
      if (!gamme && data.length) setGamme(data.find((p) => p.featured)?.gamme_key || data[0].gamme_key);
    });
  }, []);

  const product = useMemo(() => products.find((p) => p.gamme_key === gamme), [products, gamme]);
  const productTotal = product ? product.price_ttc * qty : 0;

  const canReserve = product && cust.name && cust.phone && cust.email &&
    (mode === "retrait" || address);

  const reserve = async () => {
    setLoading(true);
    try {
      const body = {
        gamme_key: gamme, quantity: qty, mode, customer: cust,
        delivery: mode === "livraison" ? { postal_code: postal, address, floor } : null,
        note,
      };
      const { data } = await api.post("/orders", body);
      navigate(`/merci?ref=${encodeURIComponent(data.ref)}&name=${encodeURIComponent(data.customer_name || "")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
      setLoading(false);
    }
  };

  return (
    <div className="brand-container py-12 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <h1 className="font-serif text-4xl font-bold text-brand-green">Réserver mon set</h1>
        <p className="text-brand-green/70 -mt-2">Remplissez le formulaire : nous vous rappelons pour convenir du paiement (espèces ou Revolut) et de la remise.</p>

        <Section n="1" title="Votre gamme &amp; quantité">
          <div className="flex flex-wrap gap-2 mb-4">
            {products.map((p) => (
              <button key={p.gamme_key} data-testid={`select-gamme-${p.gamme_key}`} onClick={() => setGamme(p.gamme_key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${gamme === p.gamme_key ? "bg-brand-green text-white border-brand-green" : "border-brand-green/20 text-brand-green hover:bg-brand-green/5"}`}>
                {p.name} — {euro(p.price_ttc)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-brand-green/70">Quantité</span>
            <div className="flex items-center gap-3">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 rounded-full bg-brand-sage text-brand-green flex items-center justify-center"><Minus className="w-4 h-4" /></button>
              <span data-testid="qty-value" className="font-serif text-2xl font-bold w-8 text-center">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="w-10 h-10 rounded-full bg-brand-sage text-brand-green flex items-center justify-center"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        </Section>

        <Section n="2" title="Mode de réception">
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="mode-livraison" onClick={() => setMode("livraison")} className={`rounded-xl p-4 border text-left transition-colors ${mode === "livraison" ? "border-brand-gold bg-brand-gold/10" : "border-brand-green/20"}`}>
              <Truck className="w-5 h-5 text-brand-green mb-2" />
              <div className="font-semibold text-brand-green">Livraison</div>
              <div className="text-xs text-brand-green/60">Offerte — Paris &amp; Île-de-France</div>
            </button>
            <button data-testid="mode-retrait" onClick={() => setMode("retrait")} className={`rounded-xl p-4 border text-left transition-colors ${mode === "retrait" ? "border-brand-gold bg-brand-gold/10" : "border-brand-green/20"}`}>
              <Store className="w-5 h-5 text-brand-green mb-2" />
              <div className="font-semibold text-brand-green">Retrait 19e</div>
              <div className="text-xs text-brand-green/60">Gratuit — adresse communiquée par téléphone</div>
            </button>
          </div>

          {mode === "livraison" && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm text-brand-green/70">Code postal</label>
                <input data-testid="postal-input" value={postal} onChange={(e) => setPostal(e.target.value.replace(/\D/g, ""))} placeholder="75019" className={inputCls} maxLength={5} type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="postal-code" enterKeyHint="next" />
              </div>
              <div>
                <label className="text-sm text-brand-green/70">Adresse de livraison *</label>
                <input data-testid="address-input" value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} placeholder="N°, rue, ville" />
              </div>
              <div>
                <label className="text-sm text-brand-green/70">Étage / ascenseur (optionnel)</label>
                <input data-testid="floor-input" value={floor} onChange={(e) => setFloor(e.target.value)} className={inputCls} placeholder="3e étage, avec ascenseur" />
              </div>
            </div>
          )}
        </Section>

        <Section n="3" title="Vos coordonnées">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="cust-name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} placeholder="Nom complet *" className={inputCls} type="text" autoComplete="name" autoCapitalize="words" enterKeyHint="next" />
            <input data-testid="cust-phone" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} placeholder="Téléphone *" className={inputCls} type="tel" inputMode="tel" autoComplete="tel" enterKeyHint="next" />
            <input data-testid="cust-email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} placeholder="Email *" className={inputCls} type="email" inputMode="email" autoComplete="email" autoCapitalize="off" autoCorrect="off" spellCheck={false} enterKeyHint="next" />
            <input data-testid="cust-company" value={cust.company} onChange={(e) => setCust({ ...cust, company: e.target.value })} placeholder="Société (optionnel)" className={inputCls} type="text" autoComplete="organization" />
          </div>
        </Section>

        <Section n="4" title="Précisions / message pour le vendeur">
          <textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={4}
            placeholder="Ex : horaires de disponibilité, préférence de paiement (espèces / Revolut), demande particulière sur l'etrog…"
            className="w-full rounded-xl border border-brand-green/20 p-4 focus:outline-none focus:ring-2 focus:ring-brand-gold" />
        </Section>
      </div>

      {/* RECAP */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-2xl p-6 border border-brand-green/10 shadow-lg lg:sticky lg:top-24">
          <h2 className="font-serif text-2xl font-bold text-brand-green mb-4">Récapitulatif</h2>
          {product ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-brand-green/70">Set {product.name} × {qty}</span><span className="font-semibold">{euro(productTotal)}</span></div>
              <div className="flex justify-between border-t border-brand-green/10 pt-3">
                <span className="text-brand-green/70">Livraison</span>
                <span className="font-semibold">{mode === "retrait" ? "Retrait gratuit" : "Offerte"}</span>
              </div>
              <div className="flex justify-between border-t border-brand-green/10 pt-3 text-lg">
                <span className="font-bold text-brand-green">Total à régler à la remise</span>
                <span data-testid="amount-total" className="num text-brand-green text-2xl">{euro(productTotal)}</span>
              </div>
              <button data-testid="reserve-button" disabled={!canReserve || loading} onClick={reserve}
                className={`w-full rounded-full py-4 font-bold text-base mt-2 flex items-center justify-center gap-2 ${canReserve && !loading ? "bg-brand-gold text-brand-green hover:bg-brand-goldHover" : "bg-gray-200 text-gray-400"}`}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageSquare className="w-5 h-5" />}
                {loading ? "Envoi…" : "Réserver"}
              </button>
              <p className="text-xs text-center text-brand-green/50">Aucun paiement en ligne. Nous vous rappelons pour convenir du règlement (espèces ou Revolut) et de la remise.</p>
            </div>
          ) : <p className="text-brand-green/60">Sélectionnez une gamme.</p>}
        </div>
      </div>
    </div>
  );
}
